import { useEffect, useState } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Play, Layers, Gauge, ArrowLeft, ArrowRight, Lock, FileText, X, Info } from "lucide-react";
import { useSite, waLink, DEFAULT_COURSES, custHeaders, getCustomerToken, resolveMedia } from "../siteContext";
import { useLang, pick } from "../i18n";
import { SectionBackdrop } from "./SectionBackdrop";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const effPrice = (c) => {
  const sp = Number(c?.special_price) || 0;
  const p = Number(c?.price) || 0;
  return sp > 0 && sp < p ? sp : p;
};

export const hasSpecial = (c) => {
  const sp = Number(c?.special_price) || 0;
  return sp > 0 && sp < (Number(c?.price) || 0);
};

// First non-empty line of a text — used as the card "hook" when no subtitle is set
export const firstLine = (txt) => (txt || "").split("\n").find((l) => l.trim()) || "";

export const Courses = () => {
  const settings = useSite();
  const { lang, s } = useLang();
  const Arrow = lang === "ar" ? ArrowLeft : ArrowRight;
  const [courses, setCourses] = useState(DEFAULT_COURSES);
  const [selected, setSelected] = useState(null);
  const navigate = useNavigate();

  const buy = async (c) => {
    if (c.payment_link) {
      window.location.href = c.payment_link;
      return;
    }
    window.open(waLink(settings, s.enrollMsg(pick(lang, c, "title")), lang), "_blank", "noopener,noreferrer");
  };

  useEffect(() => {
    axios
      .get(`${API}/courses`)
      .then((res) => {
        if (Array.isArray(res.data) && res.data.length) setCourses(res.data);
      })
      .catch(() => {});
  }, []);

  return (
    <section id="courses" data-testid="courses-section" className="relative py-28 md:py-36 bg-luxury-dark border-y border-luxury-border">
      <SectionBackdrop id="courses" />
      <div className="relative max-w-7xl mx-auto px-4 md:px-8">
        <div className="grid lg:grid-cols-2 gap-14 lg:gap-20 items-center mb-20">
          <motion.div
            initial={{ opacity: 0, x: lang === "ar" ? 60 : -60 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
            className="relative"
          >
            <div className="absolute -inset-4 border border-luxury-gold/25 rounded-3xl translate-x-4 translate-y-4" />
            <div className="relative overflow-hidden rounded-3xl border border-luxury-border">
              <img
                data-testid="courses-image"
                src={resolveMedia(settings.courses_image)}
                alt={lang === "ar" ? "دورات TheMaya المسجلة" : "TheMaya recorded courses"}
                className="w-full h-[340px] md:h-[440px] object-cover"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
              <div className="absolute bottom-5 inset-x-5 flex items-center gap-3 text-white bg-luxury-black/60 backdrop-blur-md border border-white/10 rounded-2xl px-4 py-3 [text-shadow:0_1px_6px_rgba(0,0,0,0.8)]">
                <span className="flex items-center justify-center w-11 h-11 rounded-full bg-luxury-gold text-luxury-black shrink-0">
                  <Play className="w-5 h-5" strokeWidth={1.5} />
                </span>
                <div>
                  <p className="text-sm font-semibold text-white">{lang === "ar" ? "كورسات مسجلة بعناية" : "Carefully recorded courses"}</p>
                  <p className="text-xs text-white/85">{lang === "ar" ? "شاهد/شاهديها بوقتك وبالسرعة التي تناسبك" : "Watch at your own pace, anytime"}</p>
                </div>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1], delay: 0.15 }}
          >
            <p className="text-luxury-gold text-sm tracking-[0.35em] mb-6">{s.courses_eyebrow}</p>
            <h2 data-testid="courses-title" className="font-serif font-bold text-4xl sm:text-5xl lg:text-6xl text-luxury-bone leading-[1.25]">
              {s.courses_title_a} <span className="gold-gradient-text italic">{s.courses_title_b}</span>
            </h2>
            <p className="mt-6 text-base md:text-lg text-luxury-gray leading-loose">
              {s.courses_desc}
            </p>
          </motion.div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-5">
          {courses.map((c, i) => (
            <motion.article
              key={c.id}
              data-testid={`course-card-${i}`}
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ delay: (i % 5) * 0.1, duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
              className="group relative bg-luxury-surface border border-luxury-border overflow-hidden flex flex-col rounded-2xl hover:border-luxury-gold hover:-translate-y-2 hover:shadow-[0_20px_60px_rgba(212,175,55,0.12)] transition-all duration-500"
            >
              {c.image ? (
                <div className="relative h-36 overflow-hidden">
                  <img src={resolveMedia(c.image)} alt={c.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                  <span className="absolute top-3 start-3 text-[10px] tracking-[0.18em] text-luxury-gold bg-luxury-black/60 backdrop-blur-md border border-luxury-gold/30 rounded-full px-2.5 py-1">
                    {pick(lang, c, "tag")}
                  </span>
                </div>
              ) : null}
              <div className="p-5 flex flex-col flex-1 relative">
              {!c.image && (
                <span className="self-start text-[10px] tracking-[0.18em] text-luxury-gold border border-luxury-border rounded-full px-2.5 py-1 mb-3">
                  {pick(lang, c, "tag")}
                </span>
              )}
              <div className="flex items-start justify-between gap-3 mb-1">
                <h3 className="font-serif text-xl text-luxury-bone line-clamp-1">{pick(lang, c, "title")}</h3>
                {effPrice(c) > 0 && (
                  <span data-testid={`course-price-${i}`} className="shrink-0 bg-luxury-gold text-luxury-black text-xs font-bold px-3 py-1.5 rounded-full shadow">
                    {effPrice(c)} {(c.currency || "usd").toUpperCase()}
                  </span>
                )}
              </div>
              <p className="text-luxury-gold text-sm leading-relaxed flex-1 line-clamp-2">{pick(lang, c, "subtitle") || firstLine(pick(lang, c, "description"))}</p>
              {((c.video_count || 0) > 0 || (c.pdf_count || 0) > 0) && (
                <p data-testid={`course-counts-${i}`} className="mt-3 text-xs text-luxury-gold/90 flex items-center gap-4">
                  {(c.video_count || 0) > 0 && (
                    <span className="flex items-center gap-1.5">
                      <Play className="w-3.5 h-3.5" strokeWidth={1.5} />
                      {c.video_count} {s.course_lessons}
                    </span>
                  )}
                  {(c.pdf_count || 0) > 0 && (
                    <span className="flex items-center gap-1.5">
                      <FileText className="w-3.5 h-3.5" strokeWidth={1.5} />
                      {c.pdf_count} {s.course_pdfs}
                    </span>
                  )}
                </p>
              )}
              <button
                data-testid={`course-details-${i}`}
                onClick={() => setSelected(c)}
                className="mt-3 self-start inline-flex items-center gap-2 text-xs tracking-[0.2em] text-luxury-gray hover:text-luxury-gold transition-colors duration-300"
              >
                <Info className="w-3.5 h-3.5" strokeWidth={1.5} />
                {s.course_details}
              </button>
              <div className="mt-4 pt-4 border-t border-luxury-border flex items-center justify-between text-xs text-luxury-gray">
                <span className="flex items-center gap-2">
                  <Layers className="w-4 h-4 text-luxury-gold" strokeWidth={1.25} />
                  {pick(lang, c, "lessons")}
                </span>
                <span className="flex items-center gap-2">
                  <Gauge className="w-4 h-4 text-luxury-gold" strokeWidth={1.25} />
                  {pick(lang, c, "level")}
                </span>
              </div>
              {effPrice(c) > 0 ? (
                <div className="mt-4">
                  {hasSpecial(c) && (
                    <p className="mb-2 flex items-center gap-3">
                      <span className="text-luxury-gray line-through text-sm">{c.price} {(c.currency || "usd").toUpperCase()}</span>
                      <span data-testid={`course-special-${i}`} className="text-[11px] tracking-wider bg-luxury-gold text-luxury-black font-bold px-3 py-1 rounded-full">
                        {lang === "ar" ? "عرض خاص" : "SPECIAL OFFER"}
                      </span>
                    </p>
                  )}
                  <button
                    data-testid={`course-buy-${i}`}
                    onClick={() => buy(c)}
                    className="w-full inline-flex items-center justify-center gap-2 bg-luxury-gold text-luxury-black text-xs font-semibold px-5 py-2.5 rounded-full hover:bg-luxury-goldLight transition-colors duration-300"
                  >
                    <Lock className="w-3.5 h-3.5" strokeWidth={1.5} />
                    {lang === "ar"
                      ? `اشترِ/اشترِي الآن — ${effPrice(c)} ${(c.currency || "usd").toUpperCase()}`
                      : `Buy now — ${effPrice(c)} ${(c.currency || "usd").toUpperCase()}`}
                  </button>
                </div>
              ) : (
              <a
                data-testid={`course-enroll-${i}`}
                href={waLink(settings, s.enrollMsg(pick(lang, c, "title")), lang)}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-4 inline-flex items-center gap-2 text-luxury-gold text-sm font-medium group-hover:gap-4 transition-all duration-300"
              >
                <Play className="w-4 h-4" strokeWidth={1.5} />
                {s.courses_enroll}
                <Arrow className="w-4 h-4" strokeWidth={1.5} />
              </a>
              )}
              </div>
            </motion.article>
          ))}
        </div>
      </div>

      <AnimatePresence>
        {selected && (
          <motion.div
            data-testid="course-modal"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
            onClick={() => setSelected(null)}
          >
            <motion.div
              initial={{ opacity: 0, y: 40, scale: 0.96 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 20 }}
              transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
              className="relative w-full max-w-2xl max-h-[90vh] overflow-y-auto bg-luxury-surface border border-luxury-border rounded-3xl"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                data-testid="course-modal-close"
                onClick={() => setSelected(null)}
                aria-label={s.course_close}
                className="absolute top-4 end-4 z-10 w-10 h-10 rounded-full bg-luxury-black/70 border border-luxury-border text-luxury-bone flex items-center justify-center hover:text-luxury-gold hover:border-luxury-gold transition-colors"
              >
                <X className="w-5 h-5" strokeWidth={1.5} />
              </button>
              {selected.teaser ? (
                <div className="relative bg-black">
                    <p className="absolute top-4 start-4 z-10 text-[11px] tracking-[0.2em] bg-luxury-black/70 border border-luxury-border text-luxury-gold rounded-full px-3 py-1.5">
                      {s.course_teaser}
                    </p>
                    <video
                      data-testid="course-modal-teaser"
                      controls
                      autoPlay
                      muted
                      loop
                      playsInline
                      controlsList="nodownload"
                      onContextMenu={(e) => e.preventDefault()}
                      className="w-full aspect-video bg-black"
                      src={`${API}/videos/${selected.teaser.id}/stream`}
                    />
                </div>
              ) : selected.image ? (
                <div className="h-52 overflow-hidden">
                  <img src={resolveMedia(selected.image)} alt={pick(lang, selected, "title")} className="w-full h-full object-cover" />
                </div>
              ) : null}
              <div className="p-8 md:p-10">
                <span className="inline-block text-[11px] tracking-[0.2em] text-luxury-gold border border-luxury-border rounded-full px-3 py-1 mb-5">
                  {pick(lang, selected, "tag")}
                </span>
                <h3 data-testid="course-modal-title" className="font-serif text-3xl md:text-4xl text-luxury-bone mb-2">{pick(lang, selected, "title")}</h3>
                <p className="text-luxury-gold text-sm mb-5">{pick(lang, selected, "subtitle")}</p>
                <p className="text-luxury-gray text-base leading-loose whitespace-pre-line">{pick(lang, selected, "description")}</p>
                <div className="flex flex-wrap items-center gap-3 mt-7">
                  {(selected.video_count || 0) > 0 && (
                    <span data-testid="course-modal-lessons" className="flex items-center gap-2 text-sm text-luxury-bone border border-luxury-border rounded-full px-4 py-2">
                      <Play className="w-4 h-4 text-luxury-gold" strokeWidth={1.5} />
                      {selected.video_count} {s.course_lessons}
                    </span>
                  )}
                  {(selected.pdf_count || 0) > 0 && (
                    <span data-testid="course-modal-pdfs" className="flex items-center gap-2 text-sm text-luxury-bone border border-luxury-border rounded-full px-4 py-2">
                      <FileText className="w-4 h-4 text-luxury-gold" strokeWidth={1.5} />
                      {selected.pdf_count} {s.course_pdfs}
                    </span>
                  )}
                  <span className="flex items-center gap-2 text-sm text-luxury-gray border border-luxury-border rounded-full px-4 py-2">
                    <Gauge className="w-4 h-4 text-luxury-gold" strokeWidth={1.5} />
                    {pick(lang, selected, "level")}
                  </span>
                </div>
                <div className="mt-8">
                  {effPrice(selected) > 0 ? (
                    <div>
                      {hasSpecial(selected) && (
                        <p className="mb-3 flex items-center gap-3">
                          <span className="text-luxury-gray line-through">{selected.price} {(selected.currency || "usd").toUpperCase()}</span>
                          <span data-testid="course-modal-special" className="text-[11px] tracking-wider bg-luxury-gold text-luxury-black font-bold px-3 py-1 rounded-full">
                            {lang === "ar" ? "عرض خاص" : "SPECIAL OFFER"}
                          </span>
                        </p>
                      )}
                      <button
                        data-testid="course-modal-buy"
                        onClick={() => buy(selected)}
                        className="inline-flex items-center justify-center gap-2 bg-luxury-gold text-luxury-black text-sm font-semibold px-8 py-3.5 rounded-full hover:bg-luxury-goldLight transition-colors duration-300"
                      >
                        <Lock className="w-4 h-4" strokeWidth={1.5} />
                        {lang === "ar"
                          ? `اشترِ/اشترِي الآن — ${effPrice(selected)} ${(selected.currency || "usd").toUpperCase()}`
                          : `Buy now — ${effPrice(selected)} ${(selected.currency || "usd").toUpperCase()}`}
                      </button>
                    </div>
                  ) : (
                    <a
                      data-testid="course-modal-enroll"
                      href={waLink(settings, s.enrollMsg(pick(lang, selected, "title")), lang)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 bg-luxury-gold text-luxury-black text-sm font-semibold px-8 py-3.5 rounded-full hover:bg-luxury-goldLight transition-colors duration-300"
                    >
                      <Play className="w-4 h-4" strokeWidth={1.5} />
                      {s.courses_enroll}
                    </a>
                  )}
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};
